import { ChevronDown, Search } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'

/* AppShell — the web portal frame. Sidebar plus topbar plus main.

   The sidebar expands only the group containing the active item. With six
   groups and twenty-two destinations, expanding everything produces a scrolling
   list nobody reads. */

export interface NavItem { key: string; label: ReactNode; icon?: ReactNode }
export interface NavGroup { key: string; label: ReactNode; tone: string; items: NavItem[] }

export function AppShell(
  { nav, active, onNavigate, brand, title, subtitle, icon, user, searchPlaceholder,
    toolbar, children, className }:
  {
    nav: NavGroup[]; active: string; onNavigate?: (key: string) => void
    brand?: { name: ReactNode; sub?: ReactNode; mark?: ReactNode }
    title: ReactNode; subtitle?: ReactNode; icon?: ReactNode
    user?: { name: ReactNode; role?: ReactNode; initials: string }
    /** Translated. The default is English so the shell is usable before i18n is
     *  wired, but an application that renders Arabic MUST pass this — an English
     *  placeholder inside an otherwise Arabic sidebar is the first thing the client
     *  notices, and it is invisible to whoever built the screen in English. */
    searchPlaceholder?: string
    toolbar?: ReactNode; children: ReactNode; className?: string
  },
) {
  const activeGroup = nav.find((g) => g.items.some((i) => i.key === active))?.key
  const [open, setOpen] = useState<string | undefined>(activeGroup)

  return (
    <div
      className={cn(
        'grid min-h-[560px] overflow-hidden rounded-lg border border-border bg-background',
        '[grid-template-columns:220px_minmax(0,1fr)]',
        className,
      )}
    >
      {/* sidebar */}
      <nav className="flex flex-col border-e border-border bg-card">
        {brand && (
          <div className="flex items-center gap-2.5 border-b border-border px-3 py-2.5">
            <span
              aria-hidden
              className="flex size-[34px] shrink-0 items-center justify-center rounded-lg bg-linear-135 from-[#5b9bd5] to-[#3f7fb8] text-xs font-bold tracking-[0.02em] text-white"
            >
              {brand.mark}
            </span>
            <span className="min-w-0">
              <b className="block truncate text-sm font-semibold leading-tight">{brand.name}</b>
              {brand.sub && <span className="block truncate text-[10.5px] text-muted-fg">{brand.sub}</span>}
            </span>
          </div>
        )}

        <div className="px-3 py-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-fg" />
            <Input placeholder={searchPlaceholder ?? 'Search'} dir="auto" className="h-8 ps-8 text-[13px]" />
          </div>
        </div>

        <div className="flex flex-col gap-0.5 px-2 pb-3">
          {nav.map((g) => {
            const isOpen = open === g.key
            return (
              <div key={g.key}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? undefined : g.key)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[13.5px] font-medium text-foreground hover:bg-muted cursor-pointer"
                >
                  <span aria-hidden className="size-[7px] shrink-0 rounded-full" style={{ background: g.tone }} />
                  <span className="truncate">{g.label}</span>
                  <ChevronDown
                    aria-hidden
                    className={cn('ms-auto size-3.5 text-[#94a3b8] transition-transform', isOpen && 'rotate-180')}
                  />
                </button>
                {isOpen && g.items.map((i) => (
                  <button
                    key={i.key}
                    type="button"
                    aria-current={i.key === active ? 'page' : undefined}
                    onClick={() => onNavigate?.(i.key)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-lg py-[7px] pe-3 ps-[30px] text-[13px] cursor-pointer',
                      i.key === active
                        ? 'bg-secondary font-medium text-foreground'
                        : 'text-muted-fg hover:bg-muted hover:text-foreground',
                    )}
                  >
                    {i.icon}
                    <span className="truncate">{i.label}</span>
                  </button>
                ))}
              </div>
            )
          })}
        </div>
      </nav>

      {/* content */}
      <div className="flex min-w-0 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-border bg-card px-4">
          {icon && <span className="flex size-9 items-center justify-center text-primary">{icon}</span>}
          <span className="min-w-0">
            <h2 className="truncate text-base font-semibold leading-tight">{title}</h2>
            {subtitle && <span className="block truncate text-[11.5px] text-muted-fg">{subtitle}</span>}
          </span>
          <span className="flex-1" />
          {user && (
            <button type="button" className="flex items-center gap-2 rounded-hk px-2.5 py-1.5 hover:bg-muted cursor-pointer">
              <span
                aria-hidden
                className="flex size-7 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-[#1e3a5f] dark:text-[#e8f4f8]"
              >
                {user.initials}
              </span>
              <span className="hidden text-start sm:block">
                <b className="block text-[13px] font-medium leading-tight">{user.name}</b>
                {user.role && <span className="block text-[11px] text-muted-fg">{user.role}</span>}
              </span>
            </button>
          )}
        </header>

        <main className="flex min-w-0 flex-col gap-4 p-[18px]">
          {toolbar && <div className="flex flex-wrap items-center gap-2.5">{toolbar}</div>}
          {children}
        </main>
      </div>
    </div>
  )
}
